/**
 * In-memory store for per-conversation media keys (CK_conv), spec
 * docs/MEDIA_ENCRYPTION_V2.md §4.
 *
 * The key for a topic is fetched from `GET /v0/media/convkey?topic=<name>` with
 * the same authentication the SDK's LargeFileHelper uses for `/v0/file/u/`:
 * the `X-Tinode-APIKey` header plus `X-Tinode-Auth: Token <token>`.
 *
 * NOTE ON PERSISTENCE: exactly as in media-key.js, conversation keys are kept in
 * memory only. They are never written to localStorage/sessionStorage/IndexedDB,
 * because any script running on the origin can read those, so a single XSS bug
 * would leak the keys that decrypt every attachment in every topic the user has
 * open. They are cheap to re-obtain — one authenticated GET — so caching them
 * durably buys nothing and costs the whole blast-radius argument for v2.
 * (iOS keeps them in the Keychain, which is a materially different security
 * boundary; the browser has no equivalent.)
 *
 * Error taxonomy (spec §4.1) — the two error classes must never be conflated:
 *   ConvKeyUnsupportedError  404 only. The route does not exist: the server
 *                            predates v2. The caller may fall back to v1.
 *   ConvKeyUnavailableError  401/403/503/network/timeout/malformed response.
 *                            The caller MUST fail closed and refuse to send.
 */

import { KEY_SIZE, fromBase64 } from './media-crypto.js';

/** The server does not implement v2 at all (HTTP 404). Downgrade is permitted. */
export class ConvKeyUnsupportedError extends Error {
  constructor(message) {
    super(message || 'The server does not support per-conversation media keys');
    this.name = 'ConvKeyUnsupportedError';
    this.code = 'convkey_unsupported';
  }
}

/** The key could not be obtained. The caller MUST fail closed. */
export class ConvKeyUnavailableError extends Error {
  constructor(message, status) {
    super(message || 'The conversation key is unavailable');
    this.name = 'ConvKeyUnavailableError';
    this.code = 'convkey_unavailable';
    this.status = status || 0;
  }
}

// How long to wait for the endpoint before treating it as an outage.
const REQUEST_TIMEOUT_MS = 15000;

// A well-formed Tinode conversation topic name. Deliberately narrow: it is used
// verbatim as key-wrap associated data, so it must not be able to carry path
// separators, whitespace, NULs or unbounded length.
const TOPIC_RE = /^(grp|p2p|chn|usr)[A-Za-z0-9_-]{1,32}$/;

// Not conversations with shared media; the server rejects them with 400 and a
// client must never accept one as a canonical name either.
const NON_CONVERSATION_TOPICS = ['me', 'fnd', 'sys'];

/**
 * Validate the canonical topic name echoed by the server.
 *
 * @param {*} name - `topic` from the response body.
 * @param {string} requested - the name the client asked with; used when the
 *   response omits `topic` entirely (older/partial server responses).
 * @returns {string} the validated canonical name.
 * @throws {ConvKeyUnavailableError} when it is not a usable conversation name.
 */
export function canonicalTopic(name, requested) {
  const value = (typeof name == 'string' && name) ? name : requested;
  if (typeof value != 'string' || !value) {
    throw new ConvKeyUnavailableError('Conversation key response carries no topic name');
  }
  if (NON_CONVERSATION_TOPICS.indexOf(value) >= 0 || !TOPIC_RE.test(value)) {
    throw new ConvKeyUnavailableError(
      `Conversation key response names an unusable topic '${value}'`);
  }
  return value;
}

class ConvKeyStore {
  constructor() {
    // topic name -> {ck, ckid, escrow}
    this._keys = new Map();
    // topic name -> in-flight Promise, so N attachments sent at once issue one GET.
    this._inflight = new Map();
  }

  /**
   * Conversation key for a topic, from cache or from the server.
   *
   * Entries are cached under the name that was *requested* (the wire name), so
   * a later lookup by wire name still hits; the canonical name the server
   * derived the key from is carried inside the entry as `topic`.
   *
   * @param {Tinode} tinode - connected, authenticated Tinode instance.
   * @param {string} topic - topic name exactly as it appears on the wire.
   * @returns {Promise<{ck: Uint8Array, topic: string, ckid: string,
   *   escrow: {on: boolean, alg: string, kid: string, pub: string}}>} where
   *   `topic` is the CANONICAL name — the one to use as key-wrap AAD and as
   *   `enc.tpc` — which for a p2p chat differs from the requested name.
   * @throws {ConvKeyUnsupportedError} the endpoint is not registered (404).
   * @throws {ConvKeyUnavailableError} anything else.
   */
  get(tinode, topic) {
    if (!topic || typeof topic != 'string') {
      return Promise.reject(new ConvKeyUnavailableError('A topic name is required'));
    }
    const cached = this._keys.get(topic);
    if (cached) {
      return Promise.resolve(cached);
    }
    const pending = this._inflight.get(topic);
    if (pending) {
      return pending;
    }
    const p = this._fetch(tinode, topic)
      .then(entry => {
        this._keys.set(topic, entry);
        this._inflight.delete(topic);
        return entry;
      })
      .catch(err => {
        this._inflight.delete(topic);
        throw err;
      });
    this._inflight.set(topic, p);
    return p;
  }

  /** Cached entry for a topic without touching the network, or null. */
  peek(topic) {
    return this._keys.get(topic) || null;
  }

  /** Drop one topic's key, e.g. after a `ckid` mismatch (spec §4.2). */
  invalidate(topic) {
    this._keys.delete(topic);
    this._inflight.delete(topic);
  }

  /** Forget every key. Called on logout. */
  clear() {
    this._keys.clear();
    this._inflight.clear();
  }

  _url(tinode, topic) {
    const path = `/v0/media/convkey?topic=${encodeURIComponent(topic)}`;
    // Mirror LargeFileHelper.upload(): the API host is not necessarily the
    // origin serving the webapp.
    const host = tinode && tinode._host;
    if (!host) {
      return path;
    }
    const scheme = tinode._secure ? 'https://' : 'http://';
    return scheme + host + path;
  }

  _headers(tinode) {
    // Identical to LargeFileHelper's upload headers.
    const headers = {};
    const apiKey = tinode && tinode._apiKey;
    if (apiKey) {
      headers['X-Tinode-APIKey'] = apiKey;
    }
    const token = tinode && typeof tinode.getAuthToken == 'function' ? tinode.getAuthToken() : null;
    if (token && token.token) {
      headers['X-Tinode-Auth'] = `Token ${token.token}`;
    }
    return headers;
  }

  _fetch(tinode, topic) {
    if (!tinode) {
      return Promise.reject(new ConvKeyUnavailableError('Not connected'));
    }

    const controller = typeof AbortController != 'undefined' ? new AbortController() : null;
    const timer = controller ?
      setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;

    return fetch(this._url(tinode, topic), {
      method: 'GET',
      headers: this._headers(tinode),
      signal: controller ? controller.signal : undefined
    })
      .catch(err => {
        // Network failure, DNS, CORS, timeout: an outage, never a downgrade.
        throw new ConvKeyUnavailableError(
          `Could not reach the conversation key service: ${err.message}`, 0);
      })
      .then(resp => {
        if (resp.status == 404) {
          throw new ConvKeyUnsupportedError();
        }
        if (!resp.ok) {
          throw new ConvKeyUnavailableError(
            `Conversation key request failed: ${resp.status} ${resp.statusText}`, resp.status);
        }
        return resp.json().catch(err => {
          throw new ConvKeyUnavailableError(
            `Malformed conversation key response: ${err.message}`, resp.status);
        });
      })
      .then(body => this._parse(body, topic))
      .then(entry => {
        if (timer) {
          clearTimeout(timer);
        }
        return entry;
      }, err => {
        if (timer) {
          clearTimeout(timer);
        }
        throw err;
      });
  }

  _parse(body, topic) {
    if (!body || typeof body != 'object') {
      throw new ConvKeyUnavailableError('Malformed conversation key response');
    }
    if (typeof body.ck != 'string' || !body.ck) {
      throw new ConvKeyUnavailableError('Conversation key response carries no key');
    }
    const ck = fromBase64(body.ck);
    if (!ck || ck.length != KEY_SIZE) {
      throw new ConvKeyUnavailableError(
        `Conversation key must decode to ${KEY_SIZE} bytes, got ${ck ? ck.length : 0}`);
    }
    // The server canonicalizes the topic name before deriving CK_conv and echoes
    // the canonical name back (server/server/hdl_convkey.go convKeyTopicName):
    //   usrXxx -> p2pXxx   a p2p topic's wire name is the *peer's* uid, so it is
    //                      a different string on each side; deriving from it
    //                      verbatim would give the two participants two
    //                      different keys and neither could read the other's
    //                      media.
    //   chnXxx -> grpXxx   subscriptions and media live on the group.
    //   grpXxx/p2pXxx      pass through unchanged.
    // The response name is therefore authoritative and is what must be used for
    // the AOME2/kw AAD and for enc.tpc. It is validated rather than trusted: a
    // compromised or buggy server must not be able to steer the client into
    // wrapping under an arbitrary attacker-chosen AAD string.
    const canonical = canonicalTopic(body.topic, topic);
    const esc = (body.escrow && typeof body.escrow == 'object') ? body.escrow : {};
    const on = esc.on === true;
    if (on && (typeof esc.pub != 'string' || !esc.pub)) {
      // Escrow is on but there is no key to wrap to: sending would produce a
      // message without `esc`, which is exactly what "on" forbids.
      throw new ConvKeyUnavailableError('Escrow is enabled but no escrow public key was supplied');
    }
    return {
      ck: ck,
      // The canonical name. Callers MUST use this, not the name they asked with.
      topic: canonical,
      ckid: typeof body.ckid == 'string' ? body.ckid : '',
      escrow: {
        on: on,
        alg: typeof esc.alg == 'string' ? esc.alg : '',
        kid: typeof esc.kid == 'string' ? esc.kid : '',
        pub: on ? esc.pub : ''
      }
    };
  }
}

/** Process-wide singleton. */
export const convKeyStore = new ConvKeyStore();

export default convKeyStore;
